import { createFileRoute, Link } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";
import { RegisterForm } from "@/components/auth/register-form";
import { sanitizePhoneNumber, hashPassword } from "@/lib/utils";
import { useCreatePrivilegedUser } from "@/lib/api/user";
import { useIntercom } from "@/lib/hooks/use-intercom";

export const Route = createFileRoute("/_auth/register")({
  component: Register,
});

function Register() {
  const { toast } = useToast();
  const createPrivilegedUserMutation = useCreatePrivilegedUser();
  const { openMessenger } = useIntercom();

  const handleRegister = async (
    email: string,
    password: string,
    phoneNumber: string,
    countryCode: string,
  ) => {
    const sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);
    if (!sanitizedPhoneNumber) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number",
      });
      return;
    }

    try {
      const { hash, value: hashedPassword } = await hashPassword(password);
      const fullPhoneNumber = `${countryCode}${sanitizedPhoneNumber}`;

      createPrivilegedUserMutation.mutate({
        hash,
        email,
        phoneNumber: fullPhoneNumber,
        hashedPassword,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process password. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex h-screen w-full flex-row bg-[#fbf4ea]">
      <div className="flex w-1/2 flex-col items-center justify-center">
        <div className="w-[520px]">
          <h1 className="text-4xl font-semibold leading-tight text-black">
            Welcome to Somethings
          </h1>
          <p className="mt-1 text-2xl font-semibold text-[#9a8f80]">
            Register below
          </p>

          <div className="mt-10">
            <RegisterForm onRegister={handleRegister} />
          </div>

          <div className="mt-6 flex flex-col items-center gap-3 text-sm text-[#9a8f80]">
            <div>
              Already have an email & password?{" "}
              <Link to="/login" className="font-medium text-black hover:underline">
                Login here
              </Link>
            </div>
            <div>
              Need help registering?{" "}
              <button
                type="button"
                onClick={openMessenger}
                className="border-none bg-transparent p-0 font-medium text-black hover:underline"
              >
                Talk to support
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-1/2 items-center justify-center bg-[#fbf4ea]">
        <img
          src="assets/Illustrations-7.png"
          alt="Somethings illustration"
          className="h-auto w-full max-w-[520px]"
        />
      </div>
    </div>
  );
}
